<template>
  <FriedSideSheet
    :title="$t('menu')"
    :model-value="show"
    @update:model-value="$emit('toggle', $event)"
  >
    <template #subheader>
      <div class="tabs-container">
        <template v-for="tab in tabs" :key="tab.key">
          <FriedTab v-model="selectedTab" :value="tab.key" :data-external="tab.dataExternal">
            <span class="tab-content">
              <FriedParagraph>{{ tab.label }}</FriedParagraph>
              <FriedChip v-if="tab.key === 'recipients'" :label="recipients.length.toString()" />
            </span>
          </FriedTab>
        </template>
      </div>
    </template>
    <div class="signing-page-sidemenu">
      <SideMenuActions
        v-show="selectedTab === 'actions'"
        :sender="sender"
        :is-call-enabled="isCallEnabled"
        :is-download-enabled="isDownloadEnabled"
        :is-print-enabled="isPrintEnabled"
        :is-preview="isPreview"
        :is-chat-enabled="isChatEnabled"
        :is-invite-enabled="isInviteEnabled"
        :is-reject-enabled="isRejectEnabled"
        :is-signer="isSigner"
        @action="$emit('action', $event)"
        @call="$emit('call')"
      />
      <SideMenuRecipients
        v-show="selectedTab === 'recipients'"
        :recipients="recipients"
        :is-invite-enabled="isInviteEnabled"
        :is-signing-order="isSigningOrder"
        :current-recipients-turn-to-sign="currentRecipientsTurnToSign"
        :recipients-turn-to-sign="recipientsTurnToSign"
        :is-signer="isSigner"
        @invite="$emit('invite')"
      />
      <CommentHistoryWrapper
        v-if="commentHistoryVisible"
        v-show="selectedTab === 'comment-history'"
        class="comment-history-container"
        data-external="signing-page-topbar-comment-history-button"
        :disabled="isPreview"
        :recipient="recipient"
        :recipients="recipients"
        :sender="sender"
        :prevent-comments-modification="preventCommentsModification"
        @close="$emit('close')"
      />
    </div>
    <template v-if="selectedTab === 'comment-history' && showContextualComments" #footer>
      <FriedSwitch
        data-external="signing-page-topbar-hide-comments-menu-item"
        :model-value="commentsHidden"
        :label="$t('cc_hide_all_comments_markups')"
        @update:model-value="toggleHideComments"
      />
    </template>
  </FriedSideSheet>
</template>

<script lang="ts">
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useContextualCommentingStore } from '@getaccept/lib-shared/src/contextual-commenting/store/contextual-commenting.store';
import { storeToRefs } from 'pinia';
import CommentHistoryWrapper from '../../contextual-commenting/components/CommentHistoryWrapper.vue';
import SideMenuActions from './SideMenuActions.vue';
import SideMenuRecipients from './SideMenuRecipients.vue';

export default defineComponent({
  components: {
    CommentHistoryWrapper,
    SideMenuActions,
    SideMenuRecipients,
  },
  props: {
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      required: true,
      default: () => [],
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
      default: undefined,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
      default: undefined,
    },
    newCommentsCount: {
      type: Number,
      default: 0,
    },
    recipientsTurnToSign: {
      type: Object as PropType<SigningPageRecipient>,
      default: undefined,
    },
    showContextualComments: Boolean,
    isSigner: Boolean,
    isCallEnabled: Boolean,
    isDownloadEnabled: Boolean,
    isPrintEnabled: Boolean,
    showCallOption: Boolean,
    isPreview: Boolean,
    isChatEnabled: Boolean,
    isInviteEnabled: Boolean,
    isRejectEnabled: Boolean,
    preventCommentsModification: Boolean,
    isSigningOrder: Boolean,
    currentRecipientsTurnToSign: Boolean,
    show: Boolean,
  },
  emits: ['toggle', 'close', 'chat', 'call', 'invite', 'action'],
  setup(props) {
    const { t } = useI18n();

    const selectedTab = ref<'actions' | 'recipients' | 'comment-history'>('actions');
    const tabs = computed(() => [
      {
        key: 'actions',
        label: t('actions'),
        dataExternal: 'signing-page-topbar-actions-tab',
      },
      {
        key: 'recipients',
        label: t('recipients'),
        dataExternal: 'signing-page-topbar-recipients-tab',
      },
      {
        key: 'comment-history',
        label: t('cc_comment_history'),
        dataExternal: 'signing-page-topbar-comment-history-tab',
      },
    ]);

    const commentHistoryVisible = computed(
      () => props.showContextualComments && props.recipients && props.recipient && props.sender
    );

    const { toggleHideComments } = useContextualCommentingStore();
    const { commentsHidden } = storeToRefs(useContextualCommentingStore());

    return {
      commentHistoryVisible,
      Placement,
      selectedTab,
      tabs,
      t,
      toggleHideComments,
      commentsHidden,
    };
  },
});
</script>
<style lang="scss" scoped>
.tabs-container {
  margin-top: var(--spacing-100);

  .tab-content {
    display: flex;
    gap: var(--spacing-50);
    align-items: center;
    height: 20px;
  }
}
</style>
